import { useCallback, useMemo } from 'react'
import { Platform, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import {
  useGlobalSearchParams,
  usePathname,
  useUnstableGlobalHref,
} from 'expo-router'
import { reloadAppAsync } from 'expo'

import ScreenTemplate from '@shell/ScreenTemplate'
import Typography from '@ui/typography'

const getNotFoundPathname = ({
  pathname,
  globalHref,
  notFoundParam,
}: {
  pathname: string
  globalHref: string
  notFoundParam?: string | string[]
}) => {
  if (pathname !== '/') {
    return pathname
  }

  if (typeof notFoundParam === 'string') {
    return notFoundParam.startsWith('/')
      ? notFoundParam
      : `/${notFoundParam}`
  }

  if (Array.isArray(notFoundParam) && notFoundParam.length > 0) {
    return `/${notFoundParam.join('/')}`
  }

  if (globalHref.startsWith('/')) {
    return globalHref.split('?')[0] ?? pathname
  }

  try {
    return new URL(globalHref).pathname
  } catch {
    return pathname
  }
}

export default function NotFoundScreen() {
  const { t } = useTranslation(['ui', 'app'])
  const text = 'app:notFound'
  const pathname = usePathname()
  const globalHref = useUnstableGlobalHref()
  const { 'not-found': notFoundParam } = useGlobalSearchParams<{
    'not-found'?: string | string[]
  }>()
  const displayPathname = useMemo(
    () =>
      getNotFoundPathname({
        pathname,
        globalHref,
        notFoundParam,
      }),
    [globalHref, notFoundParam, pathname]
  )

  const onRestart = useCallback(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.replace('/')
      return
    }

    reloadAppAsync('not found restart')
  }, [])

  return (
    <ScreenTemplate
      absolute
      icon="zoom-question"
      title={t(`${text}.header.title`, 'Not Found')}
      desc={t(
        `${text}.header.desc`,
        "Couldn't find what you were looking for.\nPlease refresh the app and try again."
      )}
      buttons={[
        {
          label: t('ui:restart', 'Restart'),
          onPress: onRestart,
        },
      ]}
    >
      <View className="w-full rounded-lg border border-white/10 bg-white/5 py-16">
        <Typography variant="MD" className="text-center color-white">
          {displayPathname}
        </Typography>
      </View>
    </ScreenTemplate>
  )
}
